import { useAppDispatch, useAppSelector } from "@/shared/hooks/store";
import MedicalCard from "../medical-card"
import { selectIsOnlyOneMedication, selectUserUserOnboarding } from "@/store/slices/onboarding/selectors";
import { IPrescribables } from "@/shared/helpers/main";
import { useCallback, useEffect, useMemo } from "react";
import _ from "lodash";
import { CustomersController, CustomersStepTypeEnum, CustomersStepTypeEnumPaths, IPrescriptionCopayQuantity } from "@/shared/endpoints/customers";
import { ITestClaimSlot } from "@/shared/helpers/test-claims-combo";
import { URLSearchParamsInit, useNavigate } from "react-router-dom";
import { ABSOLUTE_ROUTES } from "@/shared/constants/routes";
import { IMedicationQuantityInitialValues } from "../../../../../../shared/constants/medication-quantity";
import { isCopayRequestLimitReached } from "@/shared/helpers/test-claim";
import plusIcon from '@/assets/icons/plus-2.svg';

interface IProps {
    showCopay: boolean;
    claims: Record<string, ITestClaimSlot>;
    otcCosts?: Record<string, any>;
    prescribables: IPrescribables;
    canRunClaimsBasedOnInsurancesInProfile: boolean;
    navigateToStep?: (step: CustomersStepTypeEnumPaths, index?: number, searchParams?: URLSearchParamsInit) => void;
    onQuantityChange?(medid: number, quantity: IPrescriptionCopayQuantity): void;
    copayRequestsExhausted: boolean;
    claimsRunning?: boolean;
};

const MonthlyCostComponent = ({ totalOtcCost, totalCopay }: { totalOtcCost: number | null; totalCopay: number | null; }) => {
    const defaults = [
        {
            left: "Medication Sorting",
            right: "FREE"
        },
        {
            left: "Shipping",
            right: "FREE"
        }
    ]

    defaults.push({
        left: "Estimated Copays",
        right: totalCopay !== null ? `$${totalCopay.toFixed(2)}` : 'N/A'
    })

    defaults.push({
        left: "OTC / Not Covered",
        right: totalOtcCost !== null ? `$${totalOtcCost.toFixed(2)}` : 'N/A'
    })

    if (totalCopay !== null || totalOtcCost !== null) {
        defaults.push({
            left: "Estimated Monthly Total*",
            right: `$${((totalCopay || 0) + (totalOtcCost || 0)).toFixed(2)}`
        })
    } else {
        defaults.push({
            left: "Estimated Monthly Total*",
            right: 'N/A'
        })
    }

    return (
        <div className="flex flex-col border-t-2 border-b-2 border-mint-200 py-[20px] mt-[30px] mb-[20px] mb-[30px] sm:my-[30px]">
            <div className="flex flex-col gap-[20px]">
                <h2 className="font-atkinsonBold font-bold text-22 sm:text-24 block leading-[140%] sm:leading-[113%]">Estimated Monthly Cost</h2>
                {defaults.map((e, eIndex) => {
                    let baseClassNames = 'flex justify-between text-22 sm:text-20 leading-[113%] sm:leading-[140%]'
                    if (eIndex === defaults.length - 1) {
                        baseClassNames += ' font-atkinsonBold font-bold';
                    } else {
                        baseClassNames += 'font-atkinsonHyperlegible';
                    }
                    return (
                        <div key={eIndex} className={baseClassNames}>
                            <span className="block">{e.left}</span>
                            <span className="block">{e.right}</span>
                        </div>
                    )
                })}
            </div>
            <span className="font-atkinsonHyperlegible text-16 leading-[130%] mt-[8px] max-w-[28.25rem]">
                *Estimated Monthly Total is based on available copay pricing and may not include copays that cannot currently be displayed.
            </span>
        </div>
    )
}


const ReviewMedicationList = ({ showCopay, claims, prescribables, canRunClaimsBasedOnInsurancesInProfile, navigateToStep, onQuantityChange, copayRequestsExhausted, otcCosts, claimsRunning }: IProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userOnboarding = useAppSelector(selectUserUserOnboarding);
    const isOnlyOneMedication = useAppSelector(selectIsOnlyOneMedication);
    const lastMedicationAlertString = `You are about to remove your ${isOnlyOneMedication ? '' : 'last'} medication. This will take you back to the “My MedBox” section.`;
    const medicationsLength = prescribables.includingMedbox.length + prescribables.packagedSeparately.length;

    useEffect(() => {
        // if the medications are reduced to 0, take them back to prescriptions step and mark the step so review is disabled 
        if (medicationsLength === 0 && userOnboarding) {
            const payload = _.cloneDeep(userOnboarding);
            payload.details = { ...payload.details!, currentStep: CustomersStepTypeEnum.Prescriptions };
            dispatch(CustomersController.updateMetadata(payload));
            navigate(ABSOLUTE_ROUTES.ONBOARDING.PRESCRIPTIONS.MEDICATIONS);
        }
    }, [dispatch, medicationsLength, navigate, userOnboarding]);

    const handleOnQuantityChange = useCallback(async (medid: number, value: IMedicationQuantityInitialValues) => {
        const payload = _.cloneDeep(userOnboarding!);

        const editedQuantity = {
            quantity: +value.quantity,
            daysSupply: value.fillFrequency,
        };

        payload.details = {
            ...payload.details!,
            prescriptionCopayQuantities: {
                ...(payload.details?.prescriptionCopayQuantities || {}),
                [medid]: editedQuantity,
            }
        };

        await dispatch(CustomersController.updateMetadata(payload));
        onQuantityChange?.(medid, editedQuantity);
    }, [dispatch, onQuantityChange, userOnboarding]);

    const totalOtcCost = useMemo(() => {
        if (!otcCosts || !Object.keys(otcCosts).length) return null;
        return _.reduce(otcCosts, (acc, otc) => {
            if (otc?.cost == null) {
                return acc;
            }
            return ((acc * 100) + (otc?.cost * 100)) / 100
        }, 0);
    }, [otcCosts]);

    const totalCopay = useMemo(() => {
        if (!Object.keys(claims).length || !showCopay) return null;

        const eligible = Object.values(claims).filter(slot => slot.coPay !== null && slot.rejectCodes.length === 0);
        if (!eligible.length) return null;

        return eligible.reduce((acc, slot) => ((acc * 100) + (slot.coPay! * 100)) / 100, 0);
    }, [showCopay, claims]);

    const getCopayAmount = useCallback((medid: number) => {
        const claim = claims[String(medid)];
        if (!claim || claim.coPay === null || claim.rejectCodes.length > 0) return null;
        return claim.coPay;
    }, [claims]);

    return (
        <>
            <div className="w-full my-[30px] sm:my-[2.5rem]">
                {!!prescribables.includingMedbox.length && (
                    <div className="flex flex-col gap-[15px]">
                        <h3 className="font-atkinsonBold text-lg">In Your MedBox</h3>

                        {prescribables.includingMedbox.map((e, eIndex) => (
                            <MedicalCard
                                copayRequestsExhausted={copayRequestsExhausted}
                                claimsRunning={claimsRunning}
                                key={e.medid}
                                medication={e}
                                shouldShowEditQuantityTooltip={eIndex === 0}
                                claim={claims[e.medid]}
                                otcCost={otcCosts?.[e.medid]}
                                hasEditQuantity={canRunClaimsBasedOnInsurancesInProfile && !isCopayRequestLimitReached(userOnboarding)}
                                showCopay={showCopay}
                                copayAmount={getCopayAmount(+e.medid)}
                                onQuantityChange={value => handleOnQuantityChange(+e.medid, value)}
                                deletionModalText={medicationsLength === 1 ? lastMedicationAlertString : ''}
                            />
                        )
                        )}

                    </div>
                )}

                {!!prescribables.packagedSeparately.length && (
                    <div className="flex flex-col gap-[15px] pt-[30px]">
                        <h3 className="font-atkinsonBold text-lg">Packaged Separately</h3>

                        {prescribables.packagedSeparately.map((e, eIndex) => (
                            <MedicalCard
                                copayRequestsExhausted={copayRequestsExhausted}
                                claimsRunning={claimsRunning}
                                key={e.medid}
                                medication={e}
                                shouldShowEditQuantityTooltip={!prescribables.includingMedbox.length && eIndex === 0}
                                claim={claims[e.medid]}
                                otcCost={otcCosts?.[e.medid]}
                                hasEditQuantity={canRunClaimsBasedOnInsurancesInProfile && !isCopayRequestLimitReached(userOnboarding)}
                                showCopay={showCopay}
                                copayAmount={getCopayAmount(+e.medid)}
                                onQuantityChange={value => handleOnQuantityChange(+e.medid, value)}
                                deletionModalText={medicationsLength === 1 ? lastMedicationAlertString : ''}
                            />
                        ))}
                    </div>
                )}
                <button
                    onClick={() => {
                        navigate(ABSOLUTE_ROUTES.ONBOARDING.PRESCRIPTIONS.ADD_MEDICATION_FLOW.ADD_MEDICATION);
                    }}
                    className="flex flex-row justify-start items-center my-[30px]"
                >
                    <img height={13} width={13} src={plusIcon} alt="Add" className="inline-block mr-1" />
                    <span className="block text-primary font-dmSansBold text-bold text-18 underline leading-[105%]">Add Medication</span>
                </button>
            </div>
            {showCopay && <MonthlyCostComponent totalOtcCost={totalOtcCost} totalCopay={totalCopay} />}
        </>
    )
}

export default ReviewMedicationList;